import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Eye, MessageSquare, User, Settings, Building2, AlertCircle, CreditCard, MapPin, BarChart3, Star, CalendarDays, CheckCircle } from 'lucide-react';
import axios from 'axios';
import LocationPickerMap from '../components/LocationPickerMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALIZATIONS = {
  mechatronics: ['mechanical_electrical_assembly', 'maintenance'],
  mechanical: ['mechanical_assembly', 'maintenance', 'mechanical_designer'],
  electrical: ['switchboards', 'machine_line_edge', 'robotics', 'assembly', 'maintenance', 'electrical_designer'],
  software: ['software_development', 'plc_programming', 'robotics_programming', 'support', 'ai_integration']
};

const REGIONS = ['europe', 'asia', 'america', 'south_america', 'africa'];

const DashboardPage = () => {
  const { t } = useLanguage();
  const { user, isAuthenticated, isProfessional, hasPaid, isSubscriptionExpired, needsRenewal, subscriptionStatus, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (isProfessional) {
      fetchProfile();
      fetchConversations();
      fetchAnalytics();
    } else {
      // Non-professional users (azienda_*) don't have a pro dashboard — send them to directory
      navigate('/directory');
    }
  }, [isAuthenticated, isProfessional, navigate, authLoading]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/professionals/me/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/professionals/me/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleSpecializationChange = (spec, subSpec, checked) => {
    setProfile(prev => {
      const newSpecs = { ...prev.specializations };
      if (!newSpecs[spec]) {
        newSpecs[spec] = [];
      }
      if (checked) {
        if (!newSpecs[spec].includes(subSpec)) {
          newSpecs[spec] = [...newSpecs[spec], subSpec];
        }
      } else {
        newSpecs[spec] = newSpecs[spec].filter(s => s !== subSpec);
        if (newSpecs[spec].length === 0) {
          delete newSpecs[spec];
        }
      }
      return { ...prev, specializations: newSpecs };
    });
  };

  const handleRegionChange = (region, checked) => {
    setProfile(prev => ({
      ...prev,
      regions: checked 
        ? [...prev.regions, region]
        : prev.regions.filter(r => r !== region)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        company_name: profile.company_name,
        address: profile.address,
        city: profile.city,
        country: profile.country,
        phone: profile.phone,
        website: profile.website,
        description: profile.description,
        specializations: profile.specializations,
        regions: profile.regions
      };
      // Include location if set
      if (profile.location_lat && profile.location_lng) {
        updatePayload.location_lat = profile.location_lat;
        updatePayload.location_lng = profile.location_lng;
      }
      await axios.put(`${API}/professionals/me/profile`, updatePayload);
      toast.success(t('dash_saved'));
    } catch (error) {
      toast.error(t('common_error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // End User Dashboard
  if (!isProfessional) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="end-user-dashboard">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 
              className="text-3xl font-semibold text-slate-900"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('dash_welcome')}, {user?.name}!
            </h1>
            <p className="text-slate-600 mt-2">{t('auth_as_user')}</p>
          </div>
          
          <Card className="border border-slate-200 rounded-md">
            <CardContent className="p-8 text-center">
              <Building2 className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                {t('pay_features_1')}
              </h2>
              <p className="text-slate-600 mb-6">
                {t('pay_features_2')}
              </p>
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                onClick={() => navigate('/directory')}
                data-testid="go-to-directory"
              >
                {t('nav_directory')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Professional Dashboard
  return (
    <div className="min-h-screen bg-slate-50" data-testid="professional-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Warning Banner */}
        {isSubscriptionExpired && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-center justify-between" data-testid="subscription-expired-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-700">Il tuo abbonamento è scaduto</p>
                <p className="text-sm text-red-600">Rinnova ora per continuare ad accedere a tutti i servizi.</p>
              </div>
            </div>
            <Button 
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => navigate('/subscription')}
              data-testid="renew-subscription-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Rinnova
            </Button>
          </div>
        )}

        {needsRenewal && !isSubscriptionExpired && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-md p-4 flex items-center justify-between" data-testid="subscription-renewal-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-orange-700">Il tuo abbonamento sta per scadere</p>
                <p className="text-sm text-orange-600">
                  {subscriptionStatus?.days_remaining} giorni rimanenti. Rinnova per non perdere l'accesso.
                </p>
              </div>
            </div>
            <Button 
              size="sm"
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              onClick={() => navigate('/subscription')}
              data-testid="renew-subscription-warning-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Rinnova
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 
            className="text-3xl font-semibold text-slate-900"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('dash_welcome')}, {user?.name}!
          </h1>
          <p className="text-slate-600 mt-2">{profile?.company_name}</p>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border border-slate-200 rounded-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center">
                  <Eye className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900">{profile?.profile_views || 0}</p>
                  <p className="text-sm text-slate-500">{t('dash_profile_views')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 rounded-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900">{conversations.length}</p>
                  <p className="text-sm text-slate-500">{t('dash_messages')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="border border-slate-200 rounded-md cursor-pointer hover:border-slate-300 transition-colors"
            onClick={() => navigate(`/professional/${profile?.id}`)}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{t('dir_view_profile')}</p>
                  <p className="text-xs text-slate-500">ID: {profile?.id?.slice(0, 8)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 rounded-md p-1">
            <TabsTrigger value="analytics" className="rounded-sm" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              {t('dash_analytics') || 'Analytics'}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-sm" data-testid="tab-profile">
              <Settings className="h-4 w-4 mr-2" />
              {t('dash_edit_profile')}
            </TabsTrigger>
            <TabsTrigger value="specializations" className="rounded-sm" data-testid="tab-specs">
              {t('pro_specializations')}
            </TabsTrigger>
            <TabsTrigger value="regions" className="rounded-sm" data-testid="tab-regions">
              {t('pro_regions')}
            </TabsTrigger>
          </TabsList>
          
          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="space-y-6" data-testid="analytics-section">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border border-slate-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                          <Eye className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.profile_views}</p>
                          <p className="text-xs text-slate-500">{t('dash_total_views')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-slate-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.messages_received}</p>
                          <p className="text-xs text-slate-500">{t('dash_total_messages')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-slate-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-sm flex items-center justify-center">
                          <Star className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.average_rating.toFixed(1)}</p>
                          <p className="text-xs text-slate-500">{t('dash_avg_review')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-slate-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.review_count}</p>
                          <p className="text-xs text-slate-500">{t('dash_total_reviews')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Profile Completion + Subscription */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Completion */}
                  <Card className="border border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">{t('dash_profile_completion')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <div className="relative w-20 h-20">
                          <svg viewBox="0 0 36 36" className="w-20 h-20 transform -rotate-90">
                            <path
                              className="text-slate-100"
                              strokeDasharray="100, 100"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                            />
                            <path
                              className="text-orange-500"
                              strokeDasharray={`${analytics.profile_completion}, 100`}
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-slate-900">
                            {analytics.profile_completion}%
                          </span>
                        </div>
                        <div className="text-sm text-slate-600">
                          {analytics.profile_completion < 100 ? (
                            <p>Completa il tuo profilo per aumentare la visibilità nella directory.</p>
                          ) : (
                            <p className="text-green-600 font-medium">Profilo completo!</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Subscription Status */}
                  <Card className="border border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        {t('dash_subscription_status')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analytics.subscription?.expires_at && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">{t('dash_expires_on')}:</span>
                            <span className="text-sm font-medium text-slate-900">
                              {new Date(analytics.subscription.expires_at).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        )}
                        {analytics.subscription?.member_since && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Membro dal:</span>
                            <span className="text-sm font-medium text-slate-900">
                              {new Date(analytics.subscription.member_since).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        )}
                        {subscriptionStatus?.days_remaining !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">{t('dash_days_left')}:</span>
                            <span className={`text-sm font-bold ${subscriptionStatus.days_remaining < 30 ? 'text-orange-600' : 'text-green-600'}`}>
                              {subscriptionStatus.days_remaining}
                            </span>
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => navigate('/subscription')}
                          data-testid="analytics-subscription-btn"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {t('nav_subscription')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Reviews */}
                <Card className="border border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('dash_recent_reviews')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.recent_reviews && analytics.recent_reviews.length > 0 ? (
                      <div className="space-y-4">
                        {analytics.recent_reviews.map((review, idx) => (
                          <div key={idx} className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-slate-400" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-slate-900">{review.reviewer_name}</span>
                                <div className="flex gap-0.5">
                                  {[1,2,3,4,5].map(s => (
                                    <Star key={s} className={`h-3 w-3 ${s <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-200'}`} />
                                  ))}
                                </div>
                              </div>
                              {review.comment && (
                                <p className="text-sm text-slate-600">{review.comment}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(review.created_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">{t('dash_no_reviews_yet')}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border border-slate-200 rounded-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('dash_edit_profile')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('pro_company_name')}</Label>
                    <Input
                      value={profile?.company_name || ''}
                      onChange={(e) => handleProfileChange('company_name', e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('pro_phone')}</Label>
                    <Input
                      value={profile?.phone || ''}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('pro_address')}</Label>
                  <Input
                    value={profile?.address || ''}
                    onChange={(e) => handleProfileChange('address', e.target.value)}
                    className="rounded-sm"
                    data-testid="edit-address"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('pro_city')}</Label>
                    <Input
                      value={profile?.city || ''}
                      onChange={(e) => handleProfileChange('city', e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('pro_country')}</Label>
                    <Input
                      value={profile?.country || ''}
                      onChange={(e) => handleProfileChange('country', e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-country"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('pro_website')}</Label>
                  <Input
                    value={profile?.website || ''}
                    onChange={(e) => handleProfileChange('website', e.target.value)}
                    placeholder="https://"
                    className="rounded-sm"
                    data-testid="edit-website"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{t('pro_description')}</Label>
                  <Textarea
                    value={profile?.description || ''}
                    onChange={(e) => handleProfileChange('description', e.target.value)}
                    rows={4}
                    className="rounded-sm"
                    data-testid="edit-description"
                  />
                </div>

                {/* Location Picker */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    Posizione sulla mappa (zona approssimata ~2km)
                  </Label>
                  <p className="text-xs text-slate-500">
                    Clicca sulla mappa per indicare la tua zona. L'indirizzo esatto non sarà visibile agli utenti.
                  </p>
                  <LocationPickerMap
                    lat={profile?.location_lat}
                    lng={profile?.location_lng}
                    onLocationSelect={(lat, lng) => {
                      handleProfileChange('location_lat', lat);
                      handleProfileChange('location_lng', lng);
                    }}
                    height="300px"
                  />
                  {profile?.location_city && (
                    <p className="text-sm text-slate-600">
                      Zona rilevata: <span className="font-medium">{profile.location_city}</span>
                    </p>
                  )}
                </div>
                
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  data-testid="save-profile"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('dash_save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Specializations Tab */}
          <TabsContent value="specializations">
            <Card className="border border-slate-200 rounded-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('pro_specializations')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(SPECIALIZATIONS).map(([spec, subs]) => (
                  <div key={spec} className="border border-slate-200 rounded-md p-4">
                    <h4 className="font-medium text-slate-900 mb-3">{t(`spec_${spec}`)}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {subs.map((sub) => (
                        <div key={sub} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-${spec}-${sub}`}
                            checked={profile?.specializations?.[spec]?.includes(sub) || false}
                            onCheckedChange={(checked) => handleSpecializationChange(spec, sub, checked)}
                            data-testid={`edit-spec-${spec}-${sub}`}
                          />
                          <Label 
                            htmlFor={`edit-${spec}-${sub}`}
                            className="text-sm text-slate-700 cursor-pointer"
                          >
                            {t(`sub_${sub}`)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  data-testid="save-specs"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('dash_save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Regions Tab */}
          <TabsContent value="regions">
            <Card className="border border-slate-200 rounded-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('pro_regions')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {REGIONS.map((region) => {
                    const isChecked = profile?.regions?.includes(region) || false;
                    return (
                      <div 
                        key={region} 
                        className={`border rounded-md p-4 cursor-pointer transition-colors ${
                          isChecked 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRegionChange(region, !isChecked);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`edit-region-${region}`}
                          />
                          <Label className="text-slate-900 cursor-pointer">
                            {t(`region_${region}`)}
                          </Label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  data-testid="save-regions"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('dash_save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
